// @flow
import React, { useState, useEffect, useRef, useMemo } from 'react'
import classnames from 'classnames'
import Router from 'next/router'
import { FormattedMessage, FormattedDate, useIntl, FormattedNumber } from 'react-intl'
import styles from './buy.module.scss'
import Avatar from '@components/app/avatar'
import Button from '@components/common/button'
import Image from '@components/common/image'
import CartCtx from '@data/contexts/cart'
// import Cleave from 'cleave.js/react'
// import stripe from '@utils/stripe'
// import { createSession } from '@data/api/purchase'

interface Props {
  category: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
  onCancel: () => void
}

interface Input {
  element: HTMLInputElement
}

const Buy = ({ category, data, onCancel }: Props) => {
  const intl = useIntl()
  const { items, setItem } = CartCtx.useContainer()
  const matchItem = items.find((item) => data.id === item.data.id) || {
    price: 0,
    message: '',
  }
  const [ price, setPrice ] = useState(matchItem.price)
  const [ message, setMessage ] = useState(matchItem.message)
  const [ priceError, setPriceError ] = useState('')
  const priceRef = useRef<Input | null>(null)

  const salesPrice = useMemo(() => +(data.sales.price ?? data.price ?? 0), [ data ])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (e: any) => {
    e.preventDefault()
    // validation
    if (
      data.sales.nameyourprice &&
      !(salesPrice === 0 && +price === 0)
      && +price < Math.max(salesPrice, 50)
    ) {
      setPriceError(
        intl.formatMessage(
          {
            id: 'buy.priceError',
            defaultMessage: 'Please input ¥{price} or more',
          },
          { price: salesPrice },
        ),
      )
      return
    }

    if (!Number.isInteger(+price)) {
      setPriceError(
        intl.formatMessage(
          {
            id: 'buy.priceIntError',
            defaultMessage: 'Please input integer',
          },
        ),
      )
      return
    }
    setPriceError('')

    setItem({
      data,
      category,
      price: +price,
      message,
    })
    Router.push('/checkout')
    // const session = await createSession(category, data.id, price, message)
    // stripe.redirectToCheckout({ sessionId: session.id })
    //   .then((result) => {
    //     if (result.error) {
    //       console.error(result)
    //     }
    //     else {
    //       console.log(result)
    //     }
    //   })
  }

  useEffect(() => {
    if (priceRef.current) {
      priceRef.current.element.focus()
    }
    setPrice(salesPrice)
  }, [])

  return (
    <section
      className={`${styles.container} d-md-flex justify-content-center pb-5`}
    >
      <div
        // eslint-disable-next-line max-len
        className={`${styles.wrap} d-flex align-items-center align-items-md-start justify-content-between ps-relative`}
      >
        <header className={`${styles.item} mb-5 px-3`}>
          <Image
            className={`${styles.cover} d-block radius mx-auto mb-5 shadow`}
            src={data.coverart_crop_url || '/static/images/cover.png'}
            width={600}
            height={600}
            alt={data.title || 'cover'}
          />
          <h1 className="tx-24 tx-w700 mb-3">{data.title}</h1>
          <p className="tx-14 mb-3">
            {data.description ? (
              <>
                {data.description.substr(0, 100)}
                {data.description.length > 100 && '...'}
              </>
            ) : (
              <>
                Released&nbsp;
                <FormattedDate
                  value={data.released_at}
                  year="numeric"
                  month="short"
                  day="2-digit"
                />
              </>
            )}
          </p>
          <div
            // eslint-disable-next-line max-len
            className="d-flex align-items-center justify-content-center justify-content-md-start tx-12"
          >
            <Avatar src={data.user.user_image} width={18} className="mr-2" />
            <span>{data.user.name}</span>
          </div>
        </header>
        <div className={`${styles.purchase} px-md-3 px-5`}>
          <h3 className="mb-3">
            <FormattedMessage
              id="buy.title"
              defaultMessage="You want to buy this {category}?"
              values={{ category }}
            />
          </h3>
          <p className="mb-4">
            <FormattedMessage
              id="buy.lead"
              defaultMessage="Once you buy, you can download and listen full version online."
            />
          </p>
          <form onSubmit={onSubmit}>
            {data.sales.nameyourprice ? (
              <div className="mb-4">
                {/* eslint-disable-next-line */}
                <label htmlFor="price" className="ps-relative">
                  <span className="d-block tx-18 tx-w600 mb-3">
                    <FormattedMessage
                      id="buy.nameyourprice"
                      defaultMessage="Name Your Price"
                    />
                  </span>
                  <span
                    className={`${styles.currency} tx-32 tx-oswald tx-w700 pr-2`}
                  >
                    ¥
                  </span>
                  <input
                    // ref={priceRef}
                    id="price"
                    type="number"
                    inputMode="numeric"
                    className={classnames(
                      `${styles.input} ${styles.price} d-inline-block tx-oswald`,
                      { [styles.error]: priceError },
                    )}
                    value={price}
                    // options={{
                    //   numeral: true,
                    //   numeralThousandsGroupStyle: 'thousand',
                    // }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: any) => setPrice(e.target.value.replace(/^0+(?=\d)/, ''))}
                    autoComplete="off"
                  />
                </label>
                {priceError ? (
                  <span className="d-inline-block tx-warning tx-14">
                    {priceError}
                  </span>
                ) : (
                  <span className="d-inline-block tx-14 px-3">
                    <FormattedMessage
                      id="buy.price"
                      defaultMessage="(¥{price} or more)"
                      values={{ price: intl.formatNumber(salesPrice) }}
                    />
                  </span>
                )}
              </div>
            ) : (
              <div className="mb-4">
                <div className="tx-32 tx-w700">
                  <span className="tx-oswald pr-2">¥</span>
                  <FormattedNumber value={salesPrice} />
                </div>
              </div>
            )}
            <div className="mb-4">
              <h2 className="tx-18 tx-w600 mb-3">
                <FormattedMessage
                  id="buy.message.title"
                  defaultMessage="Send message to the artist."
                  values={{ price: salesPrice }}
                />
              </h2>
              <p className="tx-14 mb-3">
                <FormattedMessage
                  id="buy.message.description"
                  // eslint-disable-next-line max-len
                  defaultMessage="This message directly send to the artist. So you can tell how much you love or how you feel about this sound, etc..."
                />
              </p>
              <textarea
                className={styles.message}
                placeholder="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <div className="hr" />
            <div className="d-flex justify-content-between mb-4">
              <button type="button" className="btn-link" onClick={onCancel}>
                Cancel
              </button>
              <Button type="submit" className="primary-alt">
                Continue
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
}

export default Buy
